import { HeaderGlobalSearch } from 'components/global-search/HeaderGlobalSearch';
import { LearningModeToggle } from 'components/learning-mode/LearningModeToggle';
import { AutoAwesome, HelpOutline as HelpOutlineIcon } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import { useUiDesign } from 'hooks/useUiDesign';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { t } from 'utils/getTranslations';
import Profile from './Profile';
import Notifications from './actions/Notifications';
import SavedForLater from './actions/SavedForLater';
import StyledHeaderButton from './actions/StyledHeaderButton';
import Tasks from './actions/Tasks';

type HeaderContentProps = {
    /** The header dims the page behind the bar, so it has to hear the bar open and close. */
    onSearchOpenChange?: (open: boolean) => void;
};

/**
 * The classic design's way into the new one: a switch drawn off, which one click turns on. Short of
 * room, only the sparkle and the switch show and the label stays as the accessible name: up to a 640px
 * viewport in the stacked header, and from `sm` up while the header row is under 1332px wide. The second
 * is a container query on the row's content box, which the row's padding makes 48px narrower.
 */
function UseNewDesignPill({ onSwitch }: { onSwitch: () => void }) {
    const label = t('design.use-new');
    return (
        <button
            type="button"
            role="switch"
            aria-checked="false"
            aria-label={label}
            onClick={onSwitch}
            className="new-design-pill text-grey-700 focus-visible:outline-ring flex cursor-pointer items-center gap-1.5 rounded-[8px] py-1 pr-1.5 pl-2 text-[13px] leading-none font-semibold focus-visible:outline-2 focus-visible:outline-offset-2"
        >
            <AutoAwesome className="text-ai text-base" />
            <span className="max-[640px]:hidden sm:@max-[1284px]:hidden">{label}</span>
            <span aria-hidden className="bg-grey-400 flex h-4 w-7 items-center rounded-full px-0.5">
                <span className="bg-background size-3 rounded-full" />
            </span>
        </button>
    );
}

export function HeaderContent({ onSearchOpenChange }: HeaderContentProps) {
    const intl = useIntl();
    const navigate = useNavigate();
    const { isNew, canToggle, setDesign } = useUiDesign();
    const helpCenterTitle = intl.formatMessage({ id: 'help-center.title' });
    return (
        <>
            {/* The row's third column from `sm` up. The bar can be pressed against it, so its left
                padding keeps the actions' own gap between the two. */}
            <div className="flex min-h-11 items-center justify-end gap-2 sm:col-start-3 sm:row-start-1 sm:min-h-0 sm:pl-2">
                {!isNew && canToggle && <UseNewDesignPill onSwitch={() => void setDesign('NEW', 'header')} />}
                <SavedForLater />
                <Tasks />
                <Notifications />
                <LearningModeToggle />
                <Tooltip content={helpCenterTitle}>
                    {/* An icon with no count after it, so the pill's counter padding is evened out. */}
                    <StyledHeaderButton
                        aria-label={helpCenterTitle}
                        className="px-1.5"
                        onClick={() => navigate('/help-center')}
                    >
                        <HelpOutlineIcon className="text-base" />
                    </StyledHeaderButton>
                </Tooltip>
                <Profile />
            </div>
            {/* The row's middle column from `sm` up, though it follows the actions in the tab order.
                It is in the middle of the row for as long as the actions fit in a side column; past
                that they keep their width, and the bar moves left and then narrows beside them. */}
            <HeaderGlobalSearch onOpenChange={onSearchOpenChange} className="sm:col-start-2 sm:row-start-1" />
        </>
    );
}
